// ---------------------------------------------------------------------------
// Prezio Onboard Widget — usePrefetchSummaryOptions
// ---------------------------------------------------------------------------
// When the user lands on the summary step (e.g. session reopen) the
// optionsCache may be empty. This hook fetches options for every completed
// step that the summary needs for display-name resolution. Fetches run in
// parallel and silently ignore failures — the summary gracefully falls back
// to raw IDs for any step that couldn't be resolved.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import type { StepId } from '@/api/types';
import { useSessionState, useSessionActions } from '@/hooks/useSession';

/**
 * Steps whose option responses are needed to resolve display names in the
 * summary. Excludes address/address_validation (summary uses the formatted
 * address string directly) and steps that don't show resolved option names.
 */
const DISPLAY_STEPS: readonly StepId[] = [
  'country',
  'local_consumer_type',
  'dso_org',
  'dso_tariff',
  'dso_properties',
  'retailer_org',
  'retailer_tariff',
  'retailer_properties',
  'retailer_custom_type',
  'retailer_custom_prices',
  'authorities',
] as const;

/**
 * Prefetch step options that the summary page needs for display-name
 * resolution. Returns `true` while the prefetch is still in flight.
 *
 * The hook is a no-op when:
 * - The current step is not `summary`
 * - All needed options are already cached
 * - `display_values` covers all needed steps (checked per-step in SummaryStep)
 */
export function usePrefetchSummaryOptions(): boolean {
  const { session, optionsCache } = useSessionState();
  const { fetchOptions } = useSessionActions();
  const [isResolving, setIsResolving] = useState(false);
  const attemptedRef = useRef(false);

  // Snapshot the cache ref so we don't re-trigger the effect on every
  // cache update from the fetches themselves.
  const cacheRef = useRef(optionsCache);
  cacheRef.current = optionsCache;

  useEffect(() => {
    if (!session || attemptedRef.current) return;
    if (session.current_step !== 'summary') return;

    const data = session.collected_data;
    const cache = cacheRef.current;

    const missing = DISPLAY_STEPS.filter((step) => {
      // Already in cache — no need to fetch
      if (cache.has(step)) return false;
      // Only fetch if collected_data holds evidence this step was used
      return hasStepData(data, step);
    });

    if (missing.length === 0) return;

    attemptedRef.current = true;
    setIsResolving(true);

    void Promise.all(missing.map((s) => fetchOptions(s).catch(() => null)))
      .finally(() => setIsResolving(false));
  }, [session, fetchOptions]);

  return isResolving;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check whether collected_data contains values for a given step. */
function hasStepData(data: Record<string, unknown>, step: StepId): boolean {
  switch (step) {
    case 'country':
      return typeof data['country_code'] === 'string' && data['country_code'] !== '';
    case 'local_consumer_type':
      return typeof data['local_consumer_type_id'] === 'string' && data['local_consumer_type_id'] !== '';
    case 'dso_org':
    case 'dso_tariff':
    case 'dso_properties':
      return typeof data['dso'] === 'object' && data['dso'] !== null;
    case 'retailer_org':
    case 'retailer_tariff':
    case 'retailer_properties':
      return typeof data['retailer'] === 'object' && data['retailer'] !== null;
    case 'retailer_custom_type':
    case 'retailer_custom_prices':
      return typeof data['retailer'] === 'object'
        && data['retailer'] !== null
        && (data['retailer'] as Record<string, unknown>)['is_custom'] === true;
    case 'authorities':
      return typeof data['authorities'] === 'object'
        && data['authorities'] !== null
        && Object.keys(data['authorities']).length > 0;
    default:
      return false;
  }
}
